import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CircularProgress,
  Container,
  Grid,
  Hidden,
  makeStyles,
  Theme,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { QuoteFormBg } from 'svgs';
import clsx from 'clsx';
import { Checkbox, DatePicker, Select, SubmitButton, TextInput } from 'components/atoms';
import { Field, Form } from 'react-final-form';
import { useFormSender } from 'hooks';
import { API_QUOTE_ROUTE } from 'data/api-routes';
import { SelectData } from 'components/atoms/Select';
import { quoteValidation } from 'data/validators/quote';
import { parsePhoneOnType } from 'utils/phoneHelpers';
import { ReCaptcha } from 'react-recaptcha-v3';
import { equipmentList } from 'data/page-data/equipment-list';
import { NextRouter, useRouter } from 'next/router';
import { RECAPTCHA_KEY } from 'config/env';
import { IconCardProps, iconCards, TActiveTypes } from 'data/page-data/quote-data';

const useStyles = makeStyles((theme: Theme) => ({
  iconCard: {
    padding: theme.spacing(3),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    userSelect: 'none',
    cursor: 'pointer',
    transition: 'all 200ms ease-in',
    '&:hover': {
      transform: 'scale(1.02) translateY(-5px)',
      boxShadow: '0px 6px 25px rgba(0, 0, 0, 0.2)',
    },
  },
  activeCardType: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
  formCard: {
    paddingBottom: theme.spacing(10),
    [theme.breakpoints.down('xs')]: {
      boxShadow: 'none',
    },
  },
  quoteCheck: {
    flexDirection: 'column-reverse',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
}));

const selectData: SelectData[] = [
  {
    label: 'Full Truckload',
    value: 'truckload',
  },
  {
    label: 'Intermodal',
    value: 'intermodal',
  },
  {
    label: 'Less-than-truckload',
    value: 'lessTruckload',
  },
];

const QuoteForm: FC = () => {
  const formattedEquipments = equipmentList.map(({ title }) => ({ label: title, value: title }));

  const [isHuman, setIsHuman] = useState<boolean>(false);
  const [checked, setChecked] = useState<boolean>(false);
  const [pickupDate, setPickupDate] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [activeType, setActiveType] = useState<TActiveTypes | string>('truckload');
  const [selectedEquipment, setSelectedEquipment] = useState<string>('Not Selected');
  const classes = useStyles();
  const { asPath }: NextRouter = useRouter();

  const isSmallUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
  const {
    onSubmit,
    status: { error, loading, success },
  } = useFormSender(API_QUOTE_ROUTE);

  useEffect(() => {
    console.info('isHuman =', isHuman);
  }, [isHuman]);

  const handleSubmit = async (values, actions, cb) => {
    if (isHuman) {
      const additionalData = {
        chose: checked ? 'YES' : 'NO',
        loadType: selectData.find(({ value }) => value === activeType).label,
        deliveryDate,
        pickupDate,
      };
      await onSubmit(values, actions, cb, additionalData);
      setPickupDate(null);
      setDeliveryDate(null);
      setChecked(false);
      return;
    }

    throw new Error('RECAPTCHA DETECT ROBOT 🤖');
  };

  useEffect(() => {
    const isEquipment = asPath.split('#')[1];
    if (isEquipment) {
      const equipment = isEquipment.split('-').join(' ');
      const selectedEquipment = formattedEquipments.find(({ value }) => value === equipment)?.value || 'Not Selected';
      setSelectedEquipment(selectedEquipment);
      document.getElementById('quote-form')?.scrollIntoView();
    }
  }, []);

  const isSubmitted = success || loading;

  const IconCard: FC<IconCardProps> = ({ icon, text, onSelect, activeIcon }) => (
    <Card
      className={clsx(onSelect === activeType && classes.activeCardType, classes.iconCard)}
      onClick={() => setActiveType(onSelect)}
    >
      <Box display="flex" alignItems="center" flexDirection="column">
        {onSelect === activeType ? activeIcon : icon}
        <Typography variant="h3">{text}</Typography>
      </Box>
    </Card>
  );

  const verifyCallback = () => {
    setIsHuman(true);
  };

  return (
    <div id="quote-form">
      <ReCaptcha sitekey={RECAPTCHA_KEY} action="send_quote" verifyCallback={verifyCallback} />
      <Container fixed maxWidth={false}>
        <Grid spacing={6} container>
          {iconCards.map(({ activeIcon, icon, onSelect, text }, index) => (
            <Grid key={index} md={4} sm={12} xs={12} item data-aos="fade-up" data-aos-delay={(index + 1) * 100}>
              <IconCard activeIcon={activeIcon} onSelect={onSelect} text={text} icon={icon} />
            </Grid>
          ))}
        </Grid>
      </Container>
      <Box py={13} />
      <Container style={{ position: 'relative' }} disableGutters={!isSmallUp} fixed maxWidth={false}>
        <Grid container>
          <Grid item md={9} sm={12} xs={12}>
            <Card className={classes.formCard} data-aos="zoom-in">
              <Typography variant="h2">
                <span>
                  FILL OUT THE FORM AND {!isSmallUp && <br />}
                  <Typography variant="h2" component="span" color="primary">
                    RECEIVE YOUR QUOTE
                  </Typography>
                  <Box mb={isSmallUp ? 8 : 6} />
                  <Form
                    onSubmit={handleSubmit}
                    validate={quoteValidation}
                    render={({ handleSubmit, submitting }) => (
                      <form onSubmit={handleSubmit}>
                        <Grid spacing={6} container>
                          <Grid item md={6} sm={12} xs={12}>
                            <Field name="firstName">
                              {({ input, meta: { error, touched } }) => (
                                <TextInput
                                  {...input}
                                  disabled={isSubmitted}
                                  type="text"
                                  error={error && touched}
                                  hintMessage={touched && error}
                                  required
                                  label={'First Name'}
                                />
                              )}
                            </Field>
                            <Box py={1} />
                          </Grid>

                          <Grid item md={6} sm={12} xs={12}>
                            <Field name="lastName">
                              {({ input, meta: { error, touched } }) => (
                                <TextInput
                                  {...input}
                                  disabled={isSubmitted}
                                  type="text"
                                  error={error && touched}
                                  hintMessage={touched && error}
                                  required
                                  label={'Last Name'}
                                />
                              )}
                            </Field>
                            <Box py={1} />
                          </Grid>

                          <Grid item md={6} sm={12} xs={12}>
                            <Field name="companyName">
                              {({ input, meta: { error, touched } }) => (
                                <TextInput
                                  {...input}
                                  disabled={isSubmitted}
                                  required
                                  error={error && touched}
                                  hintMessage={touched && error}
                                  label={'Company Name'}
                                />
                              )}
                            </Field>
                            <Box py={1} />
                          </Grid>

                          <Grid item md={6} sm={12} xs={12}>
                            <Field parse={parsePhoneOnType} name="phone">
                              {({ input, meta: { error, touched } }) => (
                                <TextInput
                                  disabled={isSubmitted}
                                  {...input}
                                  required
                                  error={error && touched}
                                  hintMessage={touched && error}
                                  label={'Phone Number'}
                                />
                              )}
                            </Field>
                            <Box py={1} />
                          </Grid>

                          <Grid item xs={12}>
                            <Field name="email">
                              {({ input, meta: { error, touched } }) => (
                                <TextInput
                                  {...input}
                                  disabled={isSubmitted}
                                  required
                                  error={error && touched}
                                  hintMessage={touched && error}
                                  label={'Email'}
                                />
                              )}
                            </Field>
                            <Box py={1} />
                          </Grid>

                          <Grid item md={6} sm={12} xs={12}>
                            <Select
                              required
                              value={activeType}
                              onChange={(e) => setActiveType(e.target.value)}
                              selectData={selectData}
                              disabled={isSubmitted}
                              label={'Load Type'}
                            />
                            <Box py={1} />
                          </Grid>

                          <Grid item md={6} sm={12} xs={12}>
                            <Select
                              notSelected
                              value={selectedEquipment}
                              onChange={(e) => setSelectedEquipment(e.target.value)}
                              selectData={formattedEquipments}
                              disabled={isSubmitted}
                              label={'Equipment'}
                            />
                            <Box py={1} />
                          </Grid>

                          <Grid item md={6} sm={12} xs={12}>
                            <Field name="originCity">
                              {({ input, meta: { error, touched } }) => (
                                <TextInput
                                  {...input}
                                  disabled={isSubmitted}
                                  error={error && touched}
                                  hintMessage={touched && error}
                                  required
                                  label={'Origin: City/State/Zip'}
                                />
                              )}
                            </Field>
                            <Box py={1} />
                          </Grid>

                          <Grid item md={6} sm={12} xs={12}>
                            <Field name="destinationCity">
                              {({ input, meta: { error, touched } }) => (
                                <TextInput
                                  {...input}
                                  disabled={isSubmitted}
                                  required
                                  error={error && touched}
                                  hintMessage={touched && error}
                                  label={'Destination: City/State/Zip'}
                                />
                              )}
                            </Field>
                            <Box py={1} />
                          </Grid>

                          <Grid item md={6} sm={12} xs={12}>
                            <DatePicker
                              value={pickupDate}
                              min={new Date()}
                              onChange={(date) => setPickupDate(date)}
                              disabled={isSubmitted}
                              label={'Load pickup date'}
                            />
                            <Box py={1} />
                          </Grid>

                          <Grid item md={6} sm={12} xs={12}>
                            <DatePicker
                              min={pickupDate}
                              value={deliveryDate}
                              onChange={(date) => setDeliveryDate(date)}
                              disabled={isSubmitted || !pickupDate}
                              label={'Load delivery date'}
                            />
                            <Box py={1} />
                          </Grid>
                        </Grid>
                        <Box mt={6} mb={6}>
                          <Field name="message">
                            {({ input, meta: { error, touched } }) => (
                              <TextInput
                                {...input}
                                disabled={isSubmitted}
                                type="text"
                                multiline
                                error={error && touched}
                                hintMessage={touched && error}
                                label={'Your message'}
                                rows={10}
                              />
                            )}
                          </Field>
                        </Box>
                        <Box display="flex" className={classes.quoteCheck}>
                          <SubmitButton disabled={submitting} error={error} success={success}>
                            {loading && <CircularProgress size="30px" color="secondary" />}
                            {success && 'Thank you for filling out your information!'}
                            {error && 'Error message'}
                            {!loading && !success && !error && 'Send'}
                          </SubmitButton>
                          {!error && !success && (
                            <>
                              <Box mx={3} />
                              <Checkbox
                                value={checked}
                                onChange={setChecked}
                                title={'Check the box if you want us to call you back with a quote'}
                              />
                            </>
                          )}
                        </Box>
                      </form>
                    )}
                  />
                </span>
              </Typography>
            </Card>
          </Grid>
          <Hidden smDown>
            <Grid item md={3} />
          </Hidden>
        </Grid>
        <Hidden smDown>
          <Box pb={8} />
          <Box data-aos="zoom-in" data-aos-delay="400" zIndex={-1} position="absolute" top="-30px" right="0">
            <QuoteFormBg />
          </Box>
        </Hidden>
      </Container>
    </div>
  );
};

export default QuoteForm;
